import {
  OWLBERT_CLOUD_KB_EMBEDDING_DIMENSIONS,
  finalizeKnowledgebaseSearchResults,
  getKnowledgebaseContentMatchScore,
  getKnowledgebaseDocumentKey,
  getKnowledgebaseNameMatchScore,
  hostedChatSessionMetadataSetDisplayName,
  hostedUserDocumentsAdd,
  hostedUserDocumentsGet,
  logicalDocumentIdForKbFetch,
  vectorDistanceToSimilarity,
  type HostedChatSessionMetadataProcedures,
  type HostedUserDocumentsProcedures,
  type KnowledgebaseRankableChunk,
} from '@owlbert/core'
import { indexBrowserHostedUserDocumentToKb } from '../knowledgebase/indexBrowserHostedUserDocument'
import { webLog, webLogAlwaysWarn } from '../logging/webLog'

const CLOUD_EMBEDDING_DIMENSIONS = OWLBERT_CLOUD_KB_EMBEDDING_DIMENSIONS
const MAX_TOOL_ROUNDS = 8
/** Cap full-table scan for lexical rescue (keeps UI responsive on huge KBs). */
const KB_LEXICAL_SCAN_MAX = 12_000
/** Max extra rows merged into vector hits (deduped by chunk id). */
const KB_LEXICAL_EXTRAS_MAX = 150
/** Cosine distance used for lexical-only rows so `finalizeKnowledgebaseSearchResults` gates still pass. */
const KB_LEXICAL_SYNTH_DISTANCE = 0.48
/** Soft cap on Markdown body length for `create_workspace_markdown_document` (chars). */
const CREATE_MARKDOWN_DOC_BODY_MAX_CHARS = 400_000
const CREATE_MARKDOWN_DOC_NAME_MAX_CHARS = 500

/** LLM tool payload aligned with `llmToolSchema` / `OwlbertTool`. */
export const SEARCH_KNOWLEDGEBASE_LLM_TOOL = {
  id: 'search_knowledgebase',
  description:
    'Search the workspace knowledgebase (indexed documents) or fetch a full document by logical document id from a prior hit. For verbatim full library file/note text, use get_library_document with the UUID.',
  input_schema: {
    type: 'object' as const,
    properties: {
      query: {
        type: 'string',
        description:
          'Natural-language search; returns ranked text chunks. Omit when using documentId.',
      },
      documentId: {
        type: 'string',
        description:
          'Logical indexed document id from a prior search hit. Returns assembled full text. Omit when using query.',
      },
      limit: {
        type: 'number',
        description: 'Max chunk results for query search (default 10, cap 50). Ignored for documentId fetch.',
      },
    },
  },
}

/** Same contract as desktop `GetLibraryDocumentTool`: full library body by UUID, KB fallback. */
export const GET_LIBRARY_DOCUMENT_LLM_TOOL = {
  id: 'get_library_document',
  description:
    'Download the full contents of a workspace library document (user uploads / notes) by UUID. Prefer this over search-only excerpts when you need the entire file or note text.',
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'Library document id (UUID from the Documents list or from a prior search hit).',
      },
    },
  },
}

/** Lets the model rename the active hosted chat thread for the sidebar. */
export const SET_CHAT_DISPLAY_NAME_LLM_TOOL = {
  id: 'set_chat_display_name',
  description:
    'Set the short title shown for this chat in the user sidebar. Call early once the topic is clear (about six words or fewer). Call again when the conversation shifts to a substantially different subject, or when the user asks to rename the chat. Avoid spamming renames.',
  input_schema: {
    type: 'object' as const,
    properties: {
      title: {
        type: 'string',
        description: 'New chat title; keep it concise (about 60 characters or fewer). Plain text, no markdown.',
      },
    },
    required: ['title'] as const,
  },
}

/** Create a workspace library document stored as Markdown (appears under Documents; indexed for KB search when possible). */
export const CREATE_WORKSPACE_MARKDOWN_DOCUMENT_LLM_TOOL = {
  id: 'create_workspace_markdown_document',
  description:
    'Create a new document in the user workspace library with Markdown body text. Use when the user asks for a note, brief, outline, spec, meeting summary, or any artifact they can open later in Documents. Prefer concise, well-structured Markdown (headings, lists, tables when helpful). After success, tell the user the document title and that it is in their library.',
  input_schema: {
    type: 'object' as const,
    properties: {
      name: {
        type: 'string',
        description: 'Document title shown in the library list; plain text (not Markdown).',
      },
      markdown: {
        type: 'string',
        description: 'Full document body in Markdown (headings use # syntax).',
      },
    },
    required: ['name', 'markdown'] as const,
  },
}

/** Generate an image with the app default image model; result is shown in chat for the user. */
export const GENERATE_IMAGE_LLM_TOOL = {
  id: 'generate_image',
  description:
    'Generate an image from a text prompt using the workspace image model. Use when the user asks for a picture, illustration, diagram, icon, logo mockup, or other visual. The image is displayed in the chat for the user automatically.',
  input_schema: {
    type: 'object' as const,
    properties: {
      prompt: {
        type: 'string',
        description: 'Detailed description of the image to generate.',
      },
      size: {
        type: 'string',
        description: 'Optional output size (model-specific), e.g. 1024x1024. Omit for the model default.',
      },
    },
    required: ['prompt'] as const,
  },
}

/** Markdown image URLs use this scheme; content is stored in session attachments. */
export const OWLBERT_ATTACHMENT_IMAGE_PREFIX = 'owlbert-attachment:'

export function formatOwlbertAttachmentImageMarkdown(attachmentId: string, alt = 'Generated image'): string {
  return `![${alt}](${OWLBERT_ATTACHMENT_IMAGE_PREFIX}${attachmentId})`
}

type KbRow = {
  id: string
  name: string
  content: string
  metadata?: Record<string, unknown>
  usage?: Record<string, unknown>
  content_hash?: string
  distance?: number
}

/** Multimodal parts for a single turn (mirrors the backend `content_parts` schema). */
export type WebChatContentPart =
  | { type: 'text'; text: string }
  | { type: 'image'; source: { type: 'base64'; media_type: string; data: string } }

export type WebChatAutoMessage = {
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  /** When set, providers that support vision receive these parts instead of plain `content`. */
  content_parts?: WebChatContentPart[]
  tool_call_id?: string
  tool_calls?: Array<{ id: string; name: string; arguments: Record<string, unknown> }>
}

export type WebChatAutoLlmResponse = {
  content?: string
  tool_calls?: Array<{ id: string; name: string; arguments: Record<string, unknown> | string }>
  finish_reason?: string
}

export type WebChatAutoRunResult = {
  content: string
  imageAttachmentIds: string[]
  finishReason?: string | null
  /** True when the run was aborted between tool rounds via `shouldCancel`. */
  cancelled?: boolean
}

export type WebChatToolClients = {
  llm: {
    chatAuto: { mutate: (input: unknown) => Promise<WebChatAutoLlmResponse> }
  }
  embeddings: {
    createAutoEmbeddings: {
      mutate: (input: { input: string; dimensions: number }) => Promise<{ data?: Array<{ embedding?: number[] }> }>
    }
  }
  knowledgebase: {
    search: {
      mutate: (input: { queryEmbedding: number[]; limit: number; workspaceId: string }) => Promise<KbRow[]>
    }
    getAllDocuments: { query: (input: { workspaceId: string }) => Promise<KbRow[]> }
  }
  /** When set, full library fetches use hosted user documents before KB reassembly (same order as Electron). */
  owlbertHostedUserDocuments?: HostedUserDocumentsProcedures
  images?: {
    generate: {
      mutate: (input: {
        prompt: string
        size?: string
      }) => Promise<{ mime_type: string; b64_json: string; revised_prompt?: string | null }>
    }
  }
  sessionAttachments?: {
    add: {
      mutate: (input: {
        workspaceId: string
        chatSessionId: string
        name: string
        mimeType: string
        content: string
      }) => Promise<string>
    }
  }
}

/**
 * When the assistant lists no tools, expose KB search + library full-document fetch by default.
 * If it lists tools, attach the pair when either id is present (parity with desktop session tools).
 */
export function webChatKnowledgebaseToolOffered(assistantToolIds: string[]): boolean {
  if (!assistantToolIds.length) return true
  return (
    assistantToolIds.includes('search_knowledgebase') ||
    assistantToolIds.includes('get_library_document')
  )
}

/**
 * When hosted user-documents tRPC is wired, expose Markdown document creation by default (empty tool list),
 * when the assistant lists `create_workspace_markdown_document`, or when it lists workspace KB / library tools
 * (same pairing idea as `search_knowledgebase` + `get_library_document`).
 */
export function webChatMarkdownDocumentCreateToolOffered(
  assistantToolIds: string[],
  hasHostedUserDocuments: boolean
): boolean {
  if (!hasHostedUserDocuments) return false
  if (!assistantToolIds.length) return true
  return (
    assistantToolIds.includes('create_workspace_markdown_document') ||
    assistantToolIds.includes('get_library_document') ||
    assistantToolIds.includes('search_knowledgebase')
  )
}

/**
 * Expose image generation when the backend images client is wired and the assistant lists
 * `generate_image`, or when the tool list is empty (default assistant toolkit).
 */
export function webChatImageGenerationToolOffered(
  assistantToolIds: string[],
  hasImagesClient: boolean
): boolean {
  if (!hasImagesClient) return false
  if (!assistantToolIds.length) return true
  return assistantToolIds.includes('generate_image')
}

function normalizeToolArguments(raw: Record<string, unknown> | string | undefined): Record<string, unknown> {
  if (raw == null) return {}
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {}
    } catch {
      return {}
    }
  }
  return raw
}

function normalizeEmbeddingVector(embedding: number[], expected: number): number[] {
  if (embedding.length > expected) return embedding.slice(0, expected)
  if (embedding.length < expected) {
    return [...embedding, ...new Array(expected - embedding.length).fill(0)]
  }
  return embedding
}

async function getQueryEmbedding(
  embeddings: WebChatToolClients['embeddings'],
  text: string
): Promise<number[]> {
  const response = await embeddings.createAutoEmbeddings.mutate({
    input: text,
    dimensions: CLOUD_EMBEDDING_DIMENSIONS,
  })
  let embedding = response?.data?.[0]?.embedding
  if (!embedding || !Array.isArray(embedding) || embedding.length === 0) {
    throw new Error('Embeddings returned no vector for the query.')
  }
  return normalizeEmbeddingVector(embedding, CLOUD_EMBEDDING_DIMENSIONS)
}

/** Mirrors `Knowledgebase.getDocumentById` using `getAllDocuments` rows (no local VectorDb). */
function assembleDocumentByIdFromRows(
  documentId: string,
  allRows: Array<Pick<KbRow, 'id' | 'name' | 'content' | 'metadata'>>
): { id: string; name: string; content: string } | null {
  const chunks: Array<{ chunkIndex: number; content: string; name: string }> = []
  let wholeDoc: { id: string; name: string; content: string } | null = null

  for (const doc of allRows) {
    const metadata = doc.metadata || {}
    const isChunk = metadata.chunk !== undefined
    const originalId = metadata.original_document_id as string | undefined

    if (isChunk && originalId === documentId) {
      const chunkIndex =
        typeof metadata.chunk === 'number' ? metadata.chunk : parseInt(String(metadata.chunk), 10) || 0
      chunks.push({ chunkIndex, content: doc.content ?? '', name: doc.name ?? documentId })
    } else if (!isChunk && (doc.id === documentId || doc.name === documentId)) {
      wholeDoc = { id: doc.id, name: doc.name ?? documentId, content: doc.content ?? '' }
    }
  }

  if (wholeDoc) return wholeDoc
  if (chunks.length === 0) return null
  chunks.sort((a, b) => a.chunkIndex - b.chunkIndex)
  const name = chunks[0]?.name ?? documentId
  const content = chunks.map((c) => c.content).join('')
  return { id: documentId, name, content }
}

function buildLexicalExtraHits(
  query: string,
  allRows: KbRow[],
  vectorHitIds: Set<string>
): KnowledgebaseRankableChunk[] {
  const q = query.trim()
  if (!q) return []

  const scanned = allRows.length > KB_LEXICAL_SCAN_MAX ? allRows.slice(0, KB_LEXICAL_SCAN_MAX) : allRows
  const scored: Array<{ chunk: KnowledgebaseRankableChunk; lex: number }> = []

  for (const r of scanned) {
    if (vectorHitIds.has(r.id)) continue
    const name = r.name ?? ''
    const content = r.content ?? ''
    const cScore = getKnowledgebaseContentMatchScore(q, content)
    const nScore = getKnowledgebaseNameMatchScore(q, name)
    const lex = cScore * 3 + nScore * 2
    if (lex <= 0) continue
    scored.push({
      lex,
      chunk: {
        id: r.id,
        name,
        content,
        metadata: r.metadata ?? {},
        usage: r.usage,
        content_hash: r.content_hash,
        distance: KB_LEXICAL_SYNTH_DISTANCE,
      },
    })
  }

  scored.sort((a, b) => b.lex - a.lex)
  return scored.slice(0, KB_LEXICAL_EXTRAS_MAX).map((s) => s.chunk)
}

/**
 * When `finalizeKnowledgebaseSearchResults` returns nothing (e.g. single-word query with no substring
 * in chunk text but vector still retrieved neighbours), fall back to best chunk per logical document by distance.
 */
function rankedChunksFromVectorHitsFallback(
  vectorHits: KnowledgebaseRankableChunk[],
  limit: number
): KnowledgebaseRankableChunk[] {
  const byDoc = new Map<string, KnowledgebaseRankableChunk>()
  for (const h of vectorHits) {
    const key = getKnowledgebaseDocumentKey(h.id, h.metadata ?? {})
    const prev = byDoc.get(key)
    if (!prev || h.distance < prev.distance) {
      byDoc.set(key, h)
    }
  }
  return [...byDoc.values()]
    .sort((a, b) => a.distance - b.distance)
    .slice(0, Math.max(1, limit))
}

export type WebKbSearchClients = Pick<WebChatToolClients, 'embeddings' | 'knowledgebase'>

/** One ranked hit from the workspace KB (same shape as `search_knowledgebase` tool output rows). */
export type KbSearchDocumentHit = {
  documentId: string
  chunkId?: string
  content: string
  metadata: { title: string; url: string }
  purpose: string
}

/**
 * Workspace knowledgebase search: embed query → `knowledgebase.search` → `finalizeKnowledgebaseSearchResults`
 * (plus lexical rescue when needed). Used by chat tools and the web Documents library search.
 */
export async function searchWorkspaceKnowledgebaseQuery(
  clients: WebKbSearchClients,
  workspaceId: string,
  query: string,
  limit = 15
): Promise<{ documents: KbSearchDocumentHit[]; error?: string }> {
  const q = query.trim()
  if (!q) return { documents: [] }

  try {
    const cappedLimit = Math.min(50, Math.max(1, Math.floor(limit)))
    const queryEmbedding = await getQueryEmbedding(clients.embeddings, q)
    webLog('info', 'kb-search', 'query embedding ready', { dim: queryEmbedding.length, queryPreview: q.slice(0, 200) })

    const backendLimit = Math.min(cappedLimit * 8, 500)
    const searchRows = await clients.knowledgebase.search.mutate({
      queryEmbedding,
      limit: backendLimit,
      workspaceId,
    })

    const vectorHitIds = new Set(searchRows.map((r) => r.id))

    const vectorHits = searchRows.map((r) => ({
      id: r.id,
      name: r.name,
      content: r.content,
      metadata: r.metadata ?? {},
      usage: r.usage,
      content_hash: r.content_hash,
      distance: typeof r.distance === 'number' && !Number.isNaN(r.distance) ? r.distance : 1,
    }))

    const distSample = vectorHits.slice(0, 8).map((h) => ({
      id: h.id,
      distance: h.distance,
      sim: vectorDistanceToSimilarity(h.distance),
      name: h.name?.slice(0, 80),
    }))

    const wordCount = q
      .trim()
      .split(/\s+/)
      .filter((w) => w.length > 0).length
    const rankedVectorOnly = finalizeKnowledgebaseSearchResults({ query: q, limit: cappedLimit, vectorHits })
    const vectorSaturated = searchRows.length >= backendLimit
    const needsLexical =
      rankedVectorOnly.length === 0 ||
      rankedVectorOnly.length < Math.min(Math.max(1, cappedLimit), 4) ||
      wordCount <= 2 ||
      vectorSaturated

    let extraHits: KnowledgebaseRankableChunk[] = []
    let allRows: KbRow[] = []
    if (needsLexical) {
      allRows = await clients.knowledgebase.getAllDocuments.query({ workspaceId })
      if (allRows.length > KB_LEXICAL_SCAN_MAX) {
        webLogAlwaysWarn(
          'kb-search',
          `Lexical scan truncated: ${allRows.length} rows, scanning first ${KB_LEXICAL_SCAN_MAX}`,
          { workspaceId }
        )
      }
      extraHits = buildLexicalExtraHits(q, allRows, vectorHitIds)
    }

    webLog('info', 'kb-search', 'vector search summary', {
      backendLimit,
      vectorRowCount: vectorHits.length,
      allDocumentsCount: allRows.length,
      lexicalExtraCount: extraHits.length,
      needsLexical,
      vectorSaturated,
      wordCount,
      rankedVectorOnly: rankedVectorOnly.length,
      distanceSample: distSample,
    })

    let ranked =
      extraHits.length > 0
        ? finalizeKnowledgebaseSearchResults({ query: q, limit: cappedLimit, vectorHits, extraHits })
        : rankedVectorOnly

    if (ranked.length === 0 && vectorHits.length > 0) {
      webLog('info', 'kb-search', 'finalize returned 0 chunks; using vector-distance fallback', {
        query: q.slice(0, 200),
        vectorHits: vectorHits.length,
        topVector: vectorHits.slice(0, 5).map((h) => ({
          id: h.id,
          distance: h.distance,
          sim: vectorDistanceToSimilarity(h.distance),
          name: h.name?.slice(0, 120),
        })),
      })
      ranked = rankedChunksFromVectorHitsFallback(vectorHits, cappedLimit)
    } else if (ranked.length === 0 && extraHits.length > 0) {
      webLogAlwaysWarn('kb-search', 'No documents after finalize() — only lexical extras were supplied', {
        query: q.slice(0, 300),
        extraHits: extraHits.length,
      })
    }

    webLog('info', 'kb-search', 'finalize result', { returnedChunks: ranked.length, limit: cappedLimit })

    const documents: KbSearchDocumentHit[] = ranked.map((d) => {
      const chunkId = typeof d.id === 'string' && d.id.length > 0 ? d.id : undefined
      return {
        documentId: logicalDocumentIdForKbFetch(d),
        ...(chunkId ? { chunkId } : {}),
        content: d.content,
        metadata: {
          title: String(d.metadata?.title ?? d.name ?? ''),
          url: String(d.metadata?.url ?? ''),
        },
        purpose: d.name,
      }
    })

    const KB_LOG_CONTENT_PREVIEW = 800
    webLog('info', 'kb-search', 'search results (returned hits)', {
      query: q,
      limit: cappedLimit,
      hitCount: documents.length,
      hits: documents.map((doc, i) => {
        const chunk = ranked[i]
        return {
          index: i,
          chunkStorageId: chunk?.id,
          documentId: doc.documentId,
          chunkId: doc.chunkId,
          distance: chunk?.distance,
          similarity: typeof chunk?.distance === 'number' ? vectorDistanceToSimilarity(chunk.distance) : undefined,
          purpose: doc.purpose,
          title: doc.metadata.title,
          url: doc.metadata.url,
          contentChars: doc.content.length,
          contentPreview: doc.content.slice(0, KB_LOG_CONTENT_PREVIEW),
        }
      }),
    })

    return { documents }
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    webLog('error', 'kb-search', 'searchWorkspaceKnowledgebaseQuery failed', { message, query: q })
    return { documents: [], error: message }
  }
}

async function executeSearchKnowledgebaseTool(
  clients: WebChatToolClients,
  workspaceId: string,
  args: Record<string, unknown>
): Promise<string> {
  const query = typeof args.query === 'string' ? args.query : undefined
  const documentId = typeof args.documentId === 'string' ? args.documentId : undefined
  const rawLimit = args.limit
  const limit =
    typeof rawLimit === 'number' && Number.isFinite(rawLimit) && rawLimit > 0
      ? Math.min(50, Math.max(1, Math.floor(rawLimit)))
      : 10

  try {
    if (documentId != null && documentId !== '') {
      webLog('info', 'kb-tool', 'documentId fetch start', { documentId, workspaceId })
      const rows = await clients.knowledgebase.getAllDocuments.query({ workspaceId })
      webLog('info', 'kb-tool', 'documentId fetch loaded rows', { rowCount: rows.length })
      const doc = assembleDocumentByIdFromRows(documentId, rows)
      const documents = doc
        ? [
            {
              documentId: doc.id,
              content: doc.content,
              metadata: { title: doc.name, url: '' },
              purpose: doc.name,
            },
          ]
        : []
      webLog('info', 'kb-tool', 'documentId fetch done', { found: Boolean(doc), contentChars: doc?.content?.length ?? 0 })
      return JSON.stringify({ documents })
    }

    if (query == null || query === '') {
      webLog('warn', 'kb-tool', 'empty query; returning no documents')
      return JSON.stringify({ documents: [] })
    }

    const { documents, error } = await searchWorkspaceKnowledgebaseQuery(clients, workspaceId, query, limit)
    return JSON.stringify(error ? { documents, error } : { documents })
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    webLog('error', 'kb-tool', 'executeSearchKnowledgebaseTool failed', { message, query, documentId })
    return JSON.stringify({ documents: [], error: message })
  }
}

async function executeGetLibraryDocumentTool(
  clients: WebChatToolClients,
  workspaceId: string,
  args: Record<string, unknown>
): Promise<string> {
  const documentId = typeof args.documentId === 'string' ? args.documentId.trim() : ''
  if (!documentId) {
    return JSON.stringify({ error: 'documentId is required' })
  }
  try {
    const uds = clients.owlbertHostedUserDocuments
    if (uds) {
      const row = await hostedUserDocumentsGet(uds, workspaceId, documentId)
      if (row) {
        return JSON.stringify({
          documentId: row.id,
          name: row.name,
          content: typeof row.content === 'string' ? row.content : '',
          mimeType: row.mimeType ?? null,
          source: 'library',
        })
      }
    }
    const rows = await clients.knowledgebase.getAllDocuments.query({ workspaceId })
    const assembled = assembleDocumentByIdFromRows(documentId, rows)
    if (assembled) {
      return JSON.stringify({
        documentId: assembled.id,
        name: assembled.name,
        content: assembled.content,
        mimeType: null,
        source: 'knowledgebase',
      })
    }
    return JSON.stringify({ error: `Library document not found: ${documentId}` })
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    webLog('error', 'kb-tool', 'executeGetLibraryDocumentTool failed', { message, documentId })
    return JSON.stringify({ error: message })
  }
}

async function executeCreateWorkspaceMarkdownDocumentTool(
  clients: WebChatToolClients,
  workspaceId: string,
  args: Record<string, unknown>
): Promise<string> {
  const uds = clients.owlbertHostedUserDocuments
  if (!uds) {
    return JSON.stringify({ ok: false, error: 'Hosted library documents are not available in this session.' })
  }

  const nameRaw = typeof args.name === 'string' ? args.name.trim() : ''
  const markdownRaw = typeof args.markdown === 'string' ? args.markdown : ''
  if (!nameRaw) {
    return JSON.stringify({ ok: false, error: 'name is required' })
  }
  const name = nameRaw.slice(0, CREATE_MARKDOWN_DOC_NAME_MAX_CHARS)
  const markdown = markdownRaw.replace(/\r\n/g, '\n')
  if (!markdown.trim()) {
    return JSON.stringify({ ok: false, error: 'markdown must contain non-whitespace content' })
  }
  if (markdown.length > CREATE_MARKDOWN_DOC_BODY_MAX_CHARS) {
    return JSON.stringify({
      ok: false,
      error: `markdown exceeds max length (${String(CREATE_MARKDOWN_DOC_BODY_MAX_CHARS)} characters)`,
    })
  }

  const id = crypto.randomUUID()
  const metadata: Record<string, unknown> = { createdFrom: 'hosted_chat_assistant' }

  try {
    await hostedUserDocumentsAdd(uds, {
      workspaceId,
      request: {
        id,
        name,
        content: markdown,
        metadata,
        mimeType: 'text/markdown',
      },
    })
    webLog('info', 'chat-tools', 'create_workspace_markdown_document saved', {
      workspaceId,
      documentId: id,
      nameChars: name.length,
      markdownChars: markdown.length,
    })

    try {
      await indexBrowserHostedUserDocumentToKb({
        clients: { embeddings: clients.embeddings, knowledgebase: clients.knowledgebase },
        workspaceId,
        doc: { id, name, content: markdown, metadata, mimeType: 'text/markdown' },
      })
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      webLog('warn', 'chat-tools', 'create_workspace_markdown_document kb index failed', { message, documentId: id })
    }

    return JSON.stringify({
      ok: true,
      documentId: id,
      name,
      mimeType: 'text/markdown',
      message: 'Document saved to the workspace library; it should appear under Documents.',
    })
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    webLog('error', 'chat-tools', 'create_workspace_markdown_document failed', { message, documentId: id })
    return JSON.stringify({ ok: false, error: message })
  }
}

async function executeSetChatDisplayNameTool(
  workspaceId: string,
  sessionId: string,
  procedures: HostedChatSessionMetadataProcedures,
  args: Record<string, unknown>,
): Promise<string> {
  const titleRaw = typeof args.title === 'string' ? args.title.trim() : ''
  if (!titleRaw) {
    return JSON.stringify({ ok: false, error: 'title is required' })
  }
  const title = titleRaw.slice(0, 120)
  try {
    await hostedChatSessionMetadataSetDisplayName(procedures, {
      workspaceId,
      sessionId,
      name: title,
    })
    return JSON.stringify({ ok: true, title })
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    webLog('error', 'chat-tools', 'set_chat_display_name failed', { message, sessionId })
    return JSON.stringify({ ok: false, error: message })
  }
}

async function executeGenerateImageTool(
  clients: WebChatToolClients,
  workspaceId: string,
  sessionId: string | undefined,
  args: Record<string, unknown>,
  pendingAttachmentIds: string[],
): Promise<string> {
  const images = clients.images
  if (!images) {
    return JSON.stringify({ ok: false, error: 'Image generation is not available in this session.' })
  }

  const prompt = typeof args.prompt === 'string' ? args.prompt.trim() : ''
  if (!prompt) {
    return JSON.stringify({ ok: false, error: 'prompt is required' })
  }
  const size = typeof args.size === 'string' && args.size.trim() ? args.size.trim() : undefined

  try {
    const generated = await images.generate.mutate({ prompt, size })
    let attachmentId: string | null = null

    if (clients.sessionAttachments && sessionId) {
      attachmentId = await clients.sessionAttachments.add.mutate({
        workspaceId,
        chatSessionId: sessionId,
        name: 'generated-image.png',
        mimeType: generated.mime_type || 'image/png',
        content: generated.b64_json,
      })
      pendingAttachmentIds.push(attachmentId)
    }

    webLog('info', 'chat-tools', 'generate_image success', {
      workspaceId,
      sessionId,
      attachmentId,
      promptChars: prompt.length,
      b64Chars: generated.b64_json.length,
    })

    return JSON.stringify({
      ok: true,
      attachment_id: attachmentId,
      revised_prompt: generated.revised_prompt ?? undefined,
      message:
        attachmentId != null
          ? 'Image generated and displayed in the chat for the user.'
          : 'Image generated successfully.',
    })
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e)
    webLog('error', 'chat-tools', 'generate_image failed', { message, workspaceId, sessionId })
    return JSON.stringify({ ok: false, error: message })
  }
}

export async function runWebChatAutoWithKbTool(opts: {
  clients: WebChatToolClients
  workspaceId: string
  messages: WebChatAutoMessage[]
  system_prompt?: string
  temperature?: number
  assistantToolIds: string[]
  /** When set with `chatSessionMetadata`, exposes `set_chat_display_name` to the model. */
  sessionId?: string
  chatSessionMetadata?: HostedChatSessionMetadataProcedures
  /** Cooperative cancellation: checked before each tool round; when true the run aborts and returns `cancelled`. */
  shouldCancel?: () => boolean
}): Promise<WebChatAutoRunResult> {
  const kbOffered = webChatKnowledgebaseToolOffered(opts.assistantToolIds)
  const titleOffered = Boolean(opts.sessionId && opts.chatSessionMetadata)
  const docCreateOffered = webChatMarkdownDocumentCreateToolOffered(
    opts.assistantToolIds,
    Boolean(opts.clients.owlbertHostedUserDocuments)
  )
  const imageOffered = webChatImageGenerationToolOffered(
    opts.assistantToolIds,
    Boolean(opts.clients.images)
  )
  const tools = [
    ...(kbOffered ? [SEARCH_KNOWLEDGEBASE_LLM_TOOL, GET_LIBRARY_DOCUMENT_LLM_TOOL] : []),
    ...(titleOffered ? [SET_CHAT_DISPLAY_NAME_LLM_TOOL] : []),
    ...(docCreateOffered ? [CREATE_WORKSPACE_MARKDOWN_DOCUMENT_LLM_TOOL] : []),
    ...(imageOffered ? [GENERATE_IMAGE_LLM_TOOL] : []),
  ]

  const sessionTitleHint = titleOffered
    ? '\n\nYou have the tool `set_chat_display_name` to set the short title shown in the user sidebar. Use it early: as soon as the topic is clear (about six words or fewer). Use it again when the conversation moves to a clearly different major topic so the title stays accurate. Do not rename on every small tangent; avoid duplicate or near-duplicate titles in the same thread. If the user asks to rename the chat, comply with their wording (trimmed to a concise title).'
    : ''
  const markdownDocHint = docCreateOffered
    ? '\n\nYou have the tool `create_workspace_markdown_document` to save a Markdown document into the user workspace library (Documents). Use it when the user wants a durable artifact: notes, specs, drafts, meeting summaries, checklists, or structured write-ups. Pass a plain-text `name` and the full `markdown` body. Do not use it for tiny replies the chat alone can carry; use it when persistence in their library is the goal.'
    : ''
  const imageHint = imageOffered
    ? '\n\nYou have the tool `generate_image` to create images from text prompts. Use it when the user asks for a picture, illustration, diagram, icon, or other visual. Pass a detailed `prompt`. The image appears in the chat for the user; describe it briefly in your reply but do not paste base64 or raw image data.'
    : ''
  const mergedSystem = [opts.system_prompt, sessionTitleHint, markdownDocHint, imageHint].filter(Boolean).join('')

  let msgs: WebChatAutoMessage[] = [...opts.messages]
  const imageAttachmentIds: string[] = []

  webLog('info', 'chat-tools', 'runWebChatAutoWithKbTool start', {
    workspaceId: opts.workspaceId,
    messageCount: msgs.length,
    kbToolOffered: kbOffered,
    titleToolOffered: titleOffered,
    markdownDocCreateOffered: docCreateOffered,
    imageToolOffered: imageOffered,
    assistantToolIds: opts.assistantToolIds,
  })

  for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
    if (opts.shouldCancel?.()) {
      webLog('info', 'chat-tools', 'runWebChatAutoWithKbTool cancelled by user', { round })
      const imageMarkdown = imageAttachmentIds.map((id) => formatOwlbertAttachmentImageMarkdown(id)).join('\n\n')
      return { content: imageMarkdown, imageAttachmentIds: [...imageAttachmentIds], finishReason: null, cancelled: true }
    }
    webLog('info', 'chat-tools', `llm.chatAuto round ${round + 1}/${MAX_TOOL_ROUNDS}`, {
      messageCount: msgs.length,
    })
    const reply = await opts.clients.llm.chatAuto.mutate({
      messages: msgs,
      system_prompt: mergedSystem || undefined,
      temperature: opts.temperature ?? 0.4,
      ...(tools.length ? { tools } : {}),
    })

    const toolCalls = reply.tool_calls
    webLog('info', 'chat-tools', 'llm.chatAuto response', {
      contentChars: (reply.content ?? '').length,
      toolCallCount: toolCalls?.length ?? 0,
      toolNames: toolCalls?.map((t) => t.name) ?? [],
    })

    if (!toolCalls?.length) {
      webLog('info', 'chat-tools', 'runWebChatAutoWithKbTool done (no tool calls)', { roundsUsed: round + 1 })
      const imageMarkdown = imageAttachmentIds.map((id) => formatOwlbertAttachmentImageMarkdown(id)).join('\n\n')
      const text = reply.content ?? ''
      const content = [text, imageMarkdown].filter(Boolean).join('\n\n')
      return {
        content,
        imageAttachmentIds: [...imageAttachmentIds],
        finishReason: reply.finish_reason ?? null,
      }
    }

    msgs.push({
      role: 'assistant',
      content: reply.content ?? '',
      tool_calls: toolCalls.map((tc) => ({
        id: tc.id,
        name: tc.name,
        arguments: normalizeToolArguments(tc.arguments as Record<string, unknown> | string | undefined),
      })),
    })

    for (const tc of toolCalls) {
      const args = normalizeToolArguments(tc.arguments as Record<string, unknown> | string | undefined)
      webLog('info', 'chat-tools', 'tool call', { id: tc.id, name: tc.name, args })
      const payload =
        tc.name === 'search_knowledgebase'
          ? await executeSearchKnowledgebaseTool(opts.clients, opts.workspaceId, args)
          : tc.name === 'get_library_document'
            ? await executeGetLibraryDocumentTool(opts.clients, opts.workspaceId, args)
            : tc.name === 'create_workspace_markdown_document'
              ? await executeCreateWorkspaceMarkdownDocumentTool(opts.clients, opts.workspaceId, args)
              : tc.name === 'set_chat_display_name' && opts.sessionId && opts.chatSessionMetadata
                ? await executeSetChatDisplayNameTool(
                    opts.workspaceId,
                    opts.sessionId,
                    opts.chatSessionMetadata,
                    args,
                  )
                : tc.name === 'generate_image'
                  ? await executeGenerateImageTool(
                      opts.clients,
                      opts.workspaceId,
                      opts.sessionId,
                      args,
                      imageAttachmentIds,
                    )
                  : JSON.stringify({ error: 'unsupported_tool', tool: tc.name })
      webLog('info', 'chat-tools', 'tool result', {
        id: tc.id,
        name: tc.name,
        payloadChars: payload.length,
      })

      msgs.push({
        role: 'tool',
        tool_call_id: tc.id,
        content: payload,
      })
    }
  }

  webLogAlwaysWarn('chat-tools', `Stopped after ${MAX_TOOL_ROUNDS} tool rounds without final assistant text`)
  const imageMarkdown = imageAttachmentIds.map((id) => formatOwlbertAttachmentImageMarkdown(id)).join('\n\n')
  const fallback = '_(Stopped: max tool rounds without a final reply.)_'
  const content = [fallback, imageMarkdown].filter(Boolean).join('\n\n')
  return { content, imageAttachmentIds: [...imageAttachmentIds], finishReason: null }
}
